import { SankeyLink } from "#components/dashboard/sankey-diagram/sankey-link"
import { SankeyNode } from "#components/dashboard/sankey-diagram/sankey-node"
import { useSankeyTooltip } from "#components/dashboard/sankey-diagram/sankey-tooltip"
import { useIsMobile } from "#hooks/use-mobile"
import type {
  RechartsSankeyLink,
  RechartsSankeyNode,
} from "@home-finance-manager/shared/types/dashboard"
import { motion } from "framer-motion"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { ResponsiveContainer, Sankey, Tooltip } from "recharts"

interface SankeyDiagramProps {
  nodes: RechartsSankeyNode[]
  links: RechartsSankeyLink[]
}

const SankeyDiagram: React.FC<SankeyDiagramProps> = ({ nodes, links }) => {
  const isMobile = useIsMobile()
  const containerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [isContainerReady, setIsContainerReady] = useState(false)
  const { CustomTooltip, MobileTooltipOverlay } = useSankeyTooltip({ nodes })

  // Backend now provides data in recharts-ready format - no transformation needed!
  const rechartsData = {
    nodes,
    links,
  }

  // Pan handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return // Only left mouse button
      setIsDragging(true)
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y })
    },
    [position]
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      })
    },
    [isDragging, dragStart]
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Touch handlers for mobile
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0]
        setIsDragging(true)
        setDragStart({ x: touch.clientX - position.x, y: touch.clientY - position.y })
      }
    },
    [position]
  )

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging || e.touches.length !== 1) return
      e.preventDefault()
      const touch = e.touches[0]
      setPosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y,
      })
    },
    [isDragging, dragStart]
  )

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Zoom handlers - use native event listener to ensure passive: false
  const handleWheelNative = useCallback((e: WheelEvent) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? 0.9 : 1.1
    setScale((prev) => Math.max(0.5, Math.min(2, prev * delta)))
  }, [])

  // Pinch zoom for mobile
  const lastPinchDistance = useRef<number | null>(null)
  const handleTouchMovePinch = useCallback((e: TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault()
      const touch1 = e.touches[0]
      const touch2 = e.touches[1]
      const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY)

      if (lastPinchDistance.current !== null) {
        const delta = distance / lastPinchDistance.current
        setScale((prev) => Math.max(0.5, Math.min(2, prev * delta)))
      }
      lastPinchDistance.current = distance
    } else {
      lastPinchDistance.current = null
    }
  }, [])

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove)
      window.addEventListener("mouseup", handleMouseUp)
      // Use passive: false for touchmove to allow preventDefault
      window.addEventListener("touchmove", handleTouchMove, { passive: false })
      window.addEventListener("touchend", handleTouchEnd)
      return () => {
        window.removeEventListener("mousemove", handleMouseMove)
        window.removeEventListener("mouseup", handleMouseUp)
        window.removeEventListener("touchmove", handleTouchMove)
        window.removeEventListener("touchend", handleTouchEnd)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd])

  // ResponsiveContainer handles sizing automatically, no need for manual dimensions

  useEffect(() => {
    const container = containerRef.current
    if (container) {
      // Use passive: false to allow preventDefault for pinch zoom
      container.addEventListener("touchmove", handleTouchMovePinch, { passive: false })
      // Use passive: false for wheel events to allow preventDefault
      container.addEventListener("wheel", handleWheelNative, { passive: false })
      return () => {
        container.removeEventListener("touchmove", handleTouchMovePinch)
        container.removeEventListener("wheel", handleWheelNative)
      }
    }
  }, [handleTouchMovePinch, handleWheelNative])

  // Check if container is ready (has dimensions) before rendering ResponsiveContainer
  useEffect(() => {
    // Reset when data changes
    setIsContainerReady(false)

    const container = containerRef.current
    if (container && nodes.length > 0 && links.length > 0) {
      const checkDimensions = () => {
        const rect = container.getBoundingClientRect()
        if (rect.width > 0 && rect.height > 0) {
          setIsContainerReady(true)
          return true
        }
        return false
      }

      // Check immediately
      if (!checkDimensions()) {
        // Retry after a short delay if not ready
        requestAnimationFrame(() => {
          if (!checkDimensions()) {
            // Also check after animation completes (motion.div animation)
            setTimeout(checkDimensions, 600)
          }
        })
      }
    }
  }, [nodes, links])

  // Prevent default touch behavior on the container to avoid passive event warnings
  useEffect(() => {
    const container = containerRef.current
    if (container && isMobile) {
      const preventDefault = (e: TouchEvent) => {
        if (e.touches.length > 1) {
          e.preventDefault()
        }
      }
      container.addEventListener("touchstart", preventDefault, { passive: false })
      return () => {
        container.removeEventListener("touchstart", preventDefault)
      }
    }
  }, [isMobile])

  if (nodes.length === 0 || links.length === 0) {
    return (
      <div className="w-full h-full min-h-[400px] flex items-center justify-center bg-muted/20 rounded-xl">
        <p className="text-muted-foreground">No data to display</p>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full h-full min-h-[400px] flex flex-col"
    >
      <div
        ref={containerRef}
        className={`flex-1 min-h-[400px] w-full relative ${isMobile ? "overflow-x-auto overflow-y-hidden" : "overflow-hidden"} touch-none`}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        style={{
          cursor: isDragging ? "grabbing" : "grab",
          width: "100%",
          height: "100%",
        }}
      >
        {/* Base container with explicit dimensions for ResponsiveContainer */}
        {/* Apply transform to this container for pan/zoom */}
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transformOrigin: "center center",
          }}
        >
          {isContainerReady ? (
            <ResponsiveContainer
              width={isMobile ? "120%" : "100%"}
              height="100%"
              minWidth={isMobile ? 800 : 0}
              minHeight={0}
            >
              <Sankey
                data={rechartsData}
                node={<SankeyNode />}
                link={<SankeyLink />}
                nodePadding={isMobile ? 35 : 30}
                linkCurvature={0.5}
                iterations={64}
                nodeWidth={isMobile ? 18 : 20}
                margin={
                  isMobile
                    ? { top: 20, right: 180, bottom: 20, left: 180 }
                    : { top: 20, right: 150, bottom: 20, left: 150 }
                }
              >
                <Tooltip content={<CustomTooltip />} animationDuration={0} />
              </Sankey>
            </ResponsiveContainer>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-muted-foreground text-sm">Loading chart...</div>
            </div>
          )}
        </div>
        {/* Mobile tooltip overlay - rendered outside the transform container */}
        <MobileTooltipOverlay />
      </div>
    </motion.div>
  )
}

export default SankeyDiagram
